const APPROACH_ITEMS = [
  {
    title: "Ecosystem integration over creation:",
    body: "We enhance existing products and networks rather than building entirely new ones. We become a customer of data platforms on behalf of founders where possible.",
  },
  {
    title: "End-to-end journey support:",
    body: "We guide founders through the entire fundraising process, connecting them to the tools they need at each step—focusing on Kenya and Africa-dedicated products.",
  },
  {
    title: "Access democratisation:",
    body: "We explicitly position Fundy as democratizing access to investment networks, helping level the playing field for founders without strong connections.",
  },
  {
    title: "Kenya-specific context:",
    body: "We understand local market dynamics, interaction channels, and terminology. WhatsApp-native. Available in English with Swahili language support planned.",
  },
];

export const About = () => {
  return (
    <article>
      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="relative min-h-[60vh] overflow-hidden bg-black">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('/image.png')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
        <div className="relative z-10 flex min-h-[60vh] items-center px-6 pb-16 pt-32 sm:px-10 lg:px-20">
          <div className="max-w-2xl space-y-4">
            <p className="animate-fade-up text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60">
              About Fundy
            </p>
            <h1 className="animate-fade-up text-[28px] font-semibold leading-[1.12] tracking-tight text-white [animation-delay:100ms] sm:text-[36px] lg:text-[40px]">
              Democratising access{" "}<br className="hidden sm:block" />to startup funding in Kenya
            </h1>
          </div>
        </div>
      </section>

      {/* ── The Problem We're Solving ─────────────────────────── */}
      <section className="bg-white px-6 py-20 sm:px-10 lg:px-20">
        <div className="max-w-4xl space-y-5">
          <h2 className="text-[22px] font-bold tracking-tight text-body-text sm:text-[24px]">
            The Problem <span className="text-primary">We&apos;re Solving</span>
          </h2>
          <p className="text-pretty text-[16px] font-normal leading-relaxed text-body-text/80 sm:text-[17px]">
            Funding is one of the biggest hurdles for startups in Kenya. Entrepreneurs often don&apos;t
            know where to seek funding or face long and complicated application processes. The whole
            ecosystem is fragmented—sophisticated tools exist but operate in isolation, awareness is
            critically low, and success still depends heavily on who you know.
          </p>
          <p className="text-pretty text-[16px] font-normal leading-relaxed text-body-text/80 sm:text-[17px]">
            Our research found that{" "}
            <strong className="font-semibold text-body-text">
              85% of Kenyan startups rely on personal networks and referrals
            </strong>{" "}
            for investor identification. First-time founders, especially those outside Nairobi&apos;s
            tech hub, face overwhelming barriers: limited investor connections, confusing terminology,
            extensive documentation requirements, and no clear path forward.
          </p>
          <p className="text-pretty text-[16px] font-normal leading-relaxed text-body-text/80 sm:text-[17px]">
            Meanwhile, investors hesitate to back early-stage ventures due to perceived risks,
            inconsistent deal quality, and the cost of due diligence at small check sizes.
          </p>
        </div>
      </section>

      {/* ── What Fundy Does ───────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('/image.png')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628]/95 via-[#0a1628]/85 to-[#0a1628]/95" />
        <div className="relative z-10 px-6 py-20 sm:px-10 lg:px-20">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            <div className="space-y-5">
              <h2 className="text-[22px] font-bold tracking-tight text-white sm:text-[24px]">What Fundy Does</h2>
              <p className="text-pretty text-[20px] font-normal leading-snug text-white sm:text-[24px]">
                We are an AI-powered platform that guides founders through the entire fundraising journey.{" "}
                <span className="font-semibold text-primary">We don&apos;t replace existing tools, we connect them.</span>
              </p>
              <p className="text-pretty text-[16px] font-normal leading-relaxed text-white/80 sm:text-[17px]">
                <span className="font-semibold text-white">For founders:</span> Fundy provides step-by-step guidance from understanding your funding
                options to connecting with matched opportunities. Through Investment Readiness
                Scoring, you learn where you stand and what to improve before you pitch.
              </p>
              <p className="text-pretty text-[16px] font-normal leading-relaxed text-white/80 sm:text-[17px]">
                <span className="font-semibold text-white">For the ecosystem:</span> Fundy serves as a common front door to Africa&apos;s private
                market tech stack. We drive qualified deal flow to specialized platforms for
                matchmaking, due diligence, documentation, and deal execution.
              </p>
            </div>
            <div />
          </div>
        </div>
      </section>

      {/* ── Our Approach ──────────────────────────────────────── */}
      <section className="bg-white px-6 py-20 sm:px-10 lg:px-20">
        <div className="grid items-start gap-8 lg:grid-cols-[3fr_2fr] lg:gap-14">
            {/* Text list */}
            <div className="space-y-6">
              <h2 className="text-[22px] font-bold tracking-tight text-body-text sm:text-[24px]">
                Our <span className="text-primary">Approach</span>
              </h2>
              <div className="space-y-5">
                {APPROACH_ITEMS.map((item) => (
                  <p
                    key={item.title}
                    className="border-l-2 border-primary/25 pl-5 text-pretty text-[16px] leading-relaxed text-body-text/80 sm:text-[17px]"
                  >
                    <span className="font-semibold text-body-text">{item.title}</span>{" "}
                    <span className="font-normal">{item.body}</span>
                  </p>
                ))}
              </div>
            </div>
            {/* Circular photo */}
            <div className="flex justify-center pt-4 lg:justify-end lg:pt-10">
              <div className="relative">
                <div aria-hidden className="absolute -inset-5 rounded-full bg-primary/10 blur-2xl" />
                <div className="relative h-72 w-72 overflow-hidden rounded-full shadow-xl ring-1 ring-black/10 sm:h-80 sm:w-80 lg:h-96 lg:w-96">
                  <img
                    src="/image.png"
                    alt="Our team in action"
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
      </section>

      {/* ── Who We Are + Our Partners (shared dark bg) ────────── */}
      {/* ── Who We Are — solid #16174A bg ─────────────────────── */}
      <section className="bg-[#16174A] px-6 py-20 sm:px-10 lg:px-20">
        <div className="max-w-4xl space-y-4">
          <h2 className="text-[22px] font-bold tracking-tight text-white sm:text-[24px]">
            Who <span className="text-primary">We Are</span>
          </h2>
          <p className="text-pretty text-[16px] font-normal leading-relaxed text-white/80 sm:text-[17px]">
            <strong className="font-semibold text-white">Fundy</strong> is developed by{" "}
            <strong className="font-semibold text-white">UTUmoja Technologies Limited</strong>{" "}
            under the{" "}
            <strong className="font-semibold text-white">
              UK Foreign, Commonwealth &amp; Development Office&apos;s Frontier Tech Hub
              programme.
            </strong>{" "}
            The platform is designed as a digital public good with core functionality freely
            accessible to all users.
          </p>
        </div>
      </section>

      {/* ── Our Partners — background image ───────────────────── */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('/image.png')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628]/90 via-[#0a1628]/80 to-[#0a1628]/90" />
        <div className="relative z-10 px-6 py-20 sm:px-10 lg:px-20">
          <div className="space-y-4">
            <h2 className="text-[22px] font-bold tracking-tight text-white sm:text-[24px]">Our Partners</h2>
            <div className="space-y-2">
              <p className="text-[16px] font-normal leading-relaxed text-white/80 sm:text-[17px]">
                <span className="font-semibold text-white">Kenya Dealroom</span> (
                <a
                  href="https://kenya.dealroom.co"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-sm font-normal text-white underline decoration-white/40 underline-offset-4 transition-colors duration-150 hover:text-primary hover:decoration-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  kenya.dealroom.co
                </a>
                ) — Market intelligence and funding data
              </p>
              <p className="text-[16px] font-normal leading-relaxed text-white/80 sm:text-[17px]">
                <span className="font-semibold text-white">African Deal Stack Consortium</span> — Connected ecosystem of private market tech
                platforms
              </p>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
};
